import React, { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem,
  Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import { BonusCategory, BonusProgram, Campaign, WEEKDAYS, errText } from './bonusTypes';
import RuleFields, { RuleDraft, emptyRule, ruleParams, ruleReady, ruleText, sampleResult } from './RuleFields';

interface Props {
  programs: BonusProgram[];
  categories: BonusCategory[];
  campaigns: Campaign[];
  canEdit: boolean;
  onChanged: () => void;
}

const currentVersion = (p: BonusProgram) => p.versions[p.versions.length - 1];

/** The form fields that show a rule version again. */
const draftOf = (method: BonusProgram['versions'][number]['method'], params: BonusProgram['versions'][number]['params']): RuleDraft => ({
  method,
  value: String(params.amount ?? params.rate ?? params.percent ?? ''),
  tiers: (params.tiers || []).length ? (params.tiers || []).map((t) => ({ from: String(t.from), amount: String(t.amount) })) : [{ from: '', amount: '' }],
  metricLabel: params.metricLabel || '',
  milestones: (params.milestones || []).length ? (params.milestones || []).map((m) => ({ label: m.label, days: String(m.days), amount: String(m.amount) })) : [{ label: '', days: '', amount: '' }],
});

const ProgramsPanel: React.FC<Props> = ({ programs, categories, campaigns, canEdit, onChanged }) => {
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editing, setEditing] = useState<BonusProgram | 'new' | null>(null);
  const [ruleFor, setRuleFor] = useState<BonusProgram | null>(null);

  // program form
  const [f, setF] = useState<any>({});
  const set = (k: string) => (v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const [sampleQty, setSampleQty] = useState('3');
  const [busy, setBusy] = useState(false);

  const openForm = (p: BonusProgram | 'new') => {
    setMessage(null);
    if (p === 'new') {
      setF({ name: '', categoryId: '', campaignId: 'ALL', frequency: 'WEEKLY', periodEndWeekday: 6, offeredBy: 'CUSTOMER', currency: 'USD', ...emptyRule(),
        minAmount: '', maxAmount: '', capPerEmployee: '', duplicatePolicy: 'REFERENCE', referenceLabel: 'Customer / account', evidenceRequired: false, effectiveFrom: '', effectiveTo: '', description: '' });
    } else {
      const v = currentVersion(p);
      setF({ name: p.name, categoryId: p.categoryId, campaignId: p.campaignId || 'ALL', frequency: p.frequency, periodEndWeekday: p.periodEndWeekday ?? 6, offeredBy: p.offeredBy,
        currency: p.currency, ...draftOf(v.method, v.params), minAmount: p.minAmount ?? '', maxAmount: p.maxAmount ?? '', capPerEmployee: p.capPerEmployee ?? '',
        duplicatePolicy: p.duplicatePolicy, referenceLabel: p.referenceLabel || '', evidenceRequired: p.evidenceRequired, effectiveFrom: p.effectiveFrom || '', effectiveTo: p.effectiveTo || '', description: p.description || '' });
    }
    setEditing(p);
  };

  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const common = {
        name: f.name, description: f.description, offeredBy: f.offeredBy, currency: f.currency, duplicatePolicy: f.duplicatePolicy, referenceLabel: f.referenceLabel,
        evidenceRequired: f.evidenceRequired, minAmount: f.minAmount === '' ? null : Number(f.minAmount), maxAmount: f.maxAmount === '' ? null : Number(f.maxAmount),
        capPerEmployee: f.capPerEmployee === '' ? null : Number(f.capPerEmployee), effectiveFrom: f.effectiveFrom || null, effectiveTo: f.effectiveTo || null,
      };
      if (editing === 'new') {
        await bonusTrackerAPI.createProgram({
          ...common, categoryId: f.categoryId, campaignId: f.campaignId === 'ALL' ? null : f.campaignId, frequency: f.frequency,
          periodEndWeekday: f.frequency === 'WEEKLY' ? f.periodEndWeekday : null, method: f.method, params: ruleParams(f),
        });
      } else if (editing) {
        await bonusTrackerAPI.updateProgram(editing.id, common);
      }
      setEditing(null);
      setMessage({ type: 'success', text: 'Program saved.' });
      onChanged();
    } catch (e) {
      setMessage({ type: 'error', text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (p: BonusProgram) => {
    try {
      await bonusTrackerAPI.updateProgram(p.id, { active: !p.active });
      onChanged();
    } catch (e) {
      setMessage({ type: 'error', text: errText(e) });
    }
  };

  // rule change
  const [rule, setRule] = useState<RuleDraft & { effectiveFrom: string; note: string }>({ ...emptyRule(), effectiveFrom: '', note: '' });
  const [ruleError, setRuleError] = useState<string | null>(null);
  const saveRule = async () => {
    if (!ruleFor) return;
    setBusy(true);
    setRuleError(null);
    try {
      await bonusTrackerAPI.addProgramVersion(ruleFor.id, { method: rule.method, params: ruleParams(rule), effectiveFrom: rule.effectiveFrom, note: rule.note });
      setRuleFor(null);
      setMessage({ type: 'success', text: 'New rule saved. Earnings already entered keep the rule they were calculated with.' });
      onChanged();
    } catch (e) {
      setRuleError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const isNew = editing === 'new';

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          A program sets the bonus rules for one campaign: the category, week, who pays, and how each amount is worked out.
          Rate changes start on a date and never change earnings already entered.
        </Typography>
        {canEdit && <Button variant="contained" onClick={() => openForm('new')}>New program</Button>}
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Program</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Current rule</TableCell>
              <TableCell>Limits</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {programs.map((p) => {
              const v = currentVersion(p);
              return (
                <TableRow key={p.id} hover>
                  <TableCell>
                    {p.name}
                    <Typography variant="caption" display="block" color="text.secondary">{p.categoryName} · {p.offeredBy === 'CUSTOMER' ? 'paid by the customer' : 'paid in house'}</Typography>
                  </TableCell>
                  <TableCell>{p.campaignName || 'All campaigns'}</TableCell>
                  <TableCell>{p.frequency === 'WEEKLY' ? `Weekly, ends ${WEEKDAYS[p.periodEndWeekday ?? 6]}` : 'Monthly'}</TableCell>
                  <TableCell>
                    {v ? ruleText(v.method, v.params, p.currency) : '-'}
                    <Typography variant="caption" display="block" color="text.secondary">
                      version {v?.version} from {v?.effectiveFrom === '2000-01-01' ? 'the start' : v?.effectiveFrom}{p.versions.length > 1 ? ` · ${p.versions.length} versions` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {[p.minAmount != null && `min ${p.minAmount}`, p.maxAmount != null && `max ${p.maxAmount}`, p.capPerEmployee != null && `cap ${p.capPerEmployee} per employee`].filter(Boolean).join(' · ') || 'None'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.duplicatePolicy === 'REFERENCE' ? 'One payment per case' : 'Cases can repeat'}{p.evidenceRequired ? ' · evidence required' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip size="small" color={p.active ? 'success' : 'default'} label={p.active ? 'Active' : 'Inactive'} /></TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => openForm(p)}>Edit</Button>
                        <Button size="small" onClick={() => { setRuleFor(p); setRuleError(null); setRule({ ...emptyRule(), method: v.method, effectiveFrom: '', note: '' }); }}>Change rule</Button>
                        <Button size="small" color="warning" onClick={() => toggleActive(p)}>{p.active ? 'Turn off' : 'Turn on'}</Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!programs.length && (
              <TableRow><TableCell colSpan={7}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No programs yet.{canEdit ? ' Create one for a campaign.' : ''}</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {editing && (
        <Dialog open onClose={() => setEditing(null)} fullWidth maxWidth="md">
          <DialogTitle>{isNew ? 'New program' : `Edit ${f.name}`}</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
            {message?.type === 'error' && <Alert severity="error">{message.text}</Alert>}
            <TextField size="small" required label="Program name" value={f.name} onChange={(e) => set('name')(e.target.value)} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="small" disabled={!isNew}>
                <InputLabel>Category</InputLabel>
                <Select label="Category" value={f.categoryId} onChange={(e) => set('categoryId')(e.target.value)}>
                  {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small" disabled={!isNew}>
                <InputLabel>Campaign</InputLabel>
                <Select label="Campaign" value={f.campaignId} onChange={(e) => set('campaignId')(e.target.value)}>
                  <MenuItem value="ALL">All campaigns</MenuItem>
                  {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth size="small" disabled={!isNew}>
                <InputLabel>Frequency</InputLabel>
                <Select label="Frequency" value={f.frequency} onChange={(e) => set('frequency')(e.target.value)}>
                  <MenuItem value="WEEKLY">Weekly</MenuItem>
                  <MenuItem value="MONTHLY">Monthly</MenuItem>
                </Select>
              </FormControl>
              {f.frequency === 'WEEKLY' && (
                <FormControl fullWidth size="small" disabled={!isNew}>
                  <InputLabel>The week ends on</InputLabel>
                  <Select label="The week ends on" value={f.periodEndWeekday} onChange={(e) => set('periodEndWeekday')(Number(e.target.value))}>
                    {WEEKDAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <FormControl fullWidth size="small">
                <InputLabel>Who pays</InputLabel>
                <Select label="Who pays" value={f.offeredBy} onChange={(e) => set('offeredBy')(e.target.value)}>
                  <MenuItem value="CUSTOMER">The customer</MenuItem>
                  <MenuItem value="IN_HOUSE">TEKS (in house)</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" label="Currency" value={f.currency} onChange={(e) => set('currency')(e.target.value.toUpperCase().slice(0, 3))} sx={{ minWidth: 100 }} />
            </Stack>

            {isNew && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>How the amount is worked out</Typography>
                <RuleFields value={f} onChange={(patch) => setF((p: any) => ({ ...p, ...patch, ...(patch.method === 'MILESTONE' ? { duplicatePolicy: 'REFERENCE' } : {}) }))} sampleQty={sampleQty} onSampleQty={setSampleQty} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Example only, from the numbers typed here: {sampleResult(f, sampleQty, String(f.minAmount), String(f.maxAmount))}
                </Typography>
              </Paper>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" type="number" fullWidth label="Minimum per earning (optional)" value={f.minAmount} onChange={(e) => set('minAmount')(e.target.value)} />
              <TextField size="small" type="number" fullWidth label="Maximum per earning (optional)" value={f.maxAmount} onChange={(e) => set('maxAmount')(e.target.value)} />
              <TextField size="small" type="number" fullWidth label="Cap per employee in a bonus (optional)" value={f.capPerEmployee} onChange={(e) => set('capPerEmployee')(e.target.value)} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Duplicates</InputLabel>
                <Select label="Duplicates" value={f.duplicatePolicy} onChange={(e) => set('duplicatePolicy')(e.target.value)}>
                  <MenuItem value="REFERENCE">Each customer / account case is paid once</MenuItem>
                  <MenuItem value="NONE">Cases can repeat (no reference needed)</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" fullWidth label="Name of the reference (for example Customer)" value={f.referenceLabel} onChange={(e) => set('referenceLabel')(e.target.value)} />
              <FormControlLabel sx={{ whiteSpace: 'nowrap' }} control={<Checkbox checked={f.evidenceRequired} onChange={(e) => set('evidenceRequired')(e.target.checked)} />} label="Evidence required" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField size="small" type="date" fullWidth label="Program starts (optional)" InputLabelProps={{ shrink: true }} value={f.effectiveFrom} onChange={(e) => set('effectiveFrom')(e.target.value)} />
              <TextField size="small" type="date" fullWidth label="Program ends (optional)" InputLabelProps={{ shrink: true }} value={f.effectiveTo} onChange={(e) => set('effectiveTo')(e.target.value)} />
            </Stack>
            <TextField size="small" multiline minRows={2} label="Description" value={f.description} onChange={(e) => set('description')(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={busy || !f.name.trim() || (isNew && (!f.categoryId || !ruleReady(f)))}>
              {isNew ? 'Create program' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {ruleFor && (
        <Dialog open onClose={() => setRuleFor(null)} fullWidth maxWidth="sm">
          <DialogTitle>Change rule · {ruleFor.name}</DialogTitle>
          <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
            {ruleError && <Alert severity="error">{ruleError}</Alert>}
            <Box>
              {ruleFor.versions.map((v) => (
                <Typography key={v.id} variant="body2" color={v === currentVersion(ruleFor) ? 'text.primary' : 'text.secondary'}>
                  Version {v.version} · from {v.effectiveFrom === '2000-01-01' ? 'the start' : v.effectiveFrom} · {ruleText(v.method, v.params, ruleFor.currency)}{v.note ? ` · ${v.note}` : ''}
                </Typography>
              ))}
            </Box>
            <RuleFields fresh value={rule} onChange={(patch) => setRule((r) => ({ ...r, ...patch }))} sampleQty={sampleQty} onSampleQty={setSampleQty} />
            <TextField size="small" type="date" required label="Starts on" InputLabelProps={{ shrink: true }} value={rule.effectiveFrom} onChange={(e) => setRule((r) => ({ ...r, effectiveFrom: e.target.value }))}
              helperText="Earnings dated before this day keep the old rule." />
            <TextField size="small" label="Why (optional)" value={rule.note} onChange={(e) => setRule((r) => ({ ...r, note: e.target.value }))} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRuleFor(null)} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={saveRule} disabled={busy || !rule.effectiveFrom || !ruleReady(rule)}>Save new rule</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ProgramsPanel;
